import React from 'react'
import type { ColDef, ICellRendererParams, CellStyleFunc, ValueGetterParams } from 'ag-grid-community'
import { Texto } from '@gravitate-js/excalibrr'
import type { Match } from '../../../../../../api/useSupplierReconciliation/types/match'
import { useFormatter } from '@api/useFormatter'
import { addCommasToNumber } from '../utils/formatters'

// Utility function to round tiny variances to prevent false positives
const roundVariance = (value: number): number => {
  const rounded = Math.round(value * 100) / 100
  // If the absolute value is less than 0.01, treat as zero
  return Math.abs(rounded) < 0.01 ? 0 : rounded
}

// Currency cell renderer component that uses market profile currency symbol
function CurrencyCellRenderer({ value }: ICellRendererParams) {
  const { formatCurrency } = useFormatter()
  const currencySymbol = formatCurrency(null, true)

  if (value === undefined || value === null) {
    return (
      <Texto appearance='hint' align='right'>
        N/A
      </Texto>
    )
  }
  return (
    <Texto align='right'>
      {currencySymbol}
      {addCommasToNumber(value, 2)}
    </Texto>
  )
}

// Variance cell renderer component that uses market profile currency symbol
function VarianceCellRenderer({ value }: ICellRendererParams) {
  const { formatCurrency } = useFormatter()
  const currencySymbol = formatCurrency(null, true)

  if (value === undefined || value === null) {
    return (
      <Texto appearance='hint' align='right'>
        N/A
      </Texto>
    )
  }

  try {
    const rawValue = typeof value === 'number' ? value : parseFloat(String(value))

    if (isNaN(rawValue))
      return (
        <Texto appearance='hint' align='right'>
          N/A
        </Texto>
      )

    // Round tiny variances to prevent false positives
    const numValue = roundVariance(rawValue)

    const isPositive = numValue >= 0
    const formattedValue = `${isPositive ? '+' : '-'}${currencySymbol}${addCommasToNumber(Math.abs(numValue), 2)}`

    const absValue = Math.abs(numValue)
    let appearance: 'default' | 'error' | 'warning' = 'default'
    if (numValue !== 0) {
      appearance = absValue > 5000 ? 'error' : 'warning'
    }

    return (
      <Texto align='right' appearance={appearance} weight={numValue !== 0 ? 'bold' : 'normal'}>
        {formattedValue}
      </Texto>
    )
  } catch (e) {
    console.error('Error formatting variance value:', value, e)
    return (
      <Texto appearance='hint' align='right'>
        N/A
      </Texto>
    )
  }
}

export function getFinancialColumns(): ColDef<Match>[] {
  return [
    // Invoice Total column
    {
      colId: 'invoice_total',
      headerName: 'INVOICE TOTAL',
      flex: 1,
      filter: 'agNumberColumnFilter',
      valueGetter: (params: ValueGetterParams<Match>) => {
        if (!params.data) return null
        return params.data.invoice_total || 0
      },
      cellRenderer: CurrencyCellRenderer,
    },

    // BOL Total column
    {
      colId: 'bol_total',
      headerName: 'BOL TOTAL',
      flex: 1,
      filter: 'agNumberColumnFilter',
      valueGetter: (params: ValueGetterParams<Match>) => {
        if (!params.data) return null
        return params.data.bol_total || 0
      },
      cellRenderer: CurrencyCellRenderer,
    },

    // Variance column
    {
      colId: 'variance',
      headerName: 'VARIANCE',
      flex: 1,
      filter: 'agNumberColumnFilter',
      cellStyle: ((params: ICellRendererParams) => {
        if (params.value !== undefined && params.value !== null && params.value !== 0) {
          // Since valueGetter already applies roundVariance, params.value should be the rounded value
          const absValue = Math.abs(params.value)

          if (absValue > 5000) {
            return {
              backgroundColor: 'var(--theme-error-dim)',
              color: 'var(--theme-error)',
            }
          }

          return { backgroundColor: 'var(--theme-warning-dim)' }
        }

        return {}
      }) as CellStyleFunc<Match>,
      valueGetter: (params: ValueGetterParams<Match>) => {
        if (!params.data) return null

        const invoiceTotal = params.data.invoice_total || 0
        const bolTotal = params.data.bol_total || 0

        // Round tiny variances to prevent false positives
        return roundVariance(invoiceTotal - bolTotal)
      },
      cellRenderer: VarianceCellRenderer,
    },
  ]
}
